import { makeAutoObservable } from 'mobx'
import AuthService from './../../../shared/services/Auth.service'
import env from './../../../environments/environment'

// Manual structure: { id, title, path, category, content, children }
class UserManualsStore {
  constructor() {
    makeAutoObservable(this)
  }

  manuals = []
  manualTree = []
  selectedManual = null
  selectedManualPath = null
  searchQuery = ''
  searchMode = 'global' // 'global' or 'current'
  searchResults = []
  expandedSections = []
  isLoading = false
  manualContentCache = {}

  // Load manual structure from a predefined list
  // In a real implementation, this could scan the docs directory
  loadManuals = async () => {
    this.isLoading = true
    try {
      // Define manual structure
      // Paths should point to markdown files
      // Files can be in public folder (e.g., /user-manuals-docs/) or served via API
      // For development, files in /src/pages/UserManuals/docs need webpack raw-loader or copy to public
      const manualManifest = [
        {
          id: 'carrier-contacts',
          title: 'Carrier Contacts',
          filename: 'Carrier-Contacts.md',
          category: 'Features',
          children: null,
        },
        {
          id: 'first-sale-tracker',
          title: 'First Sale Tracker',
          filename: 'First-Sale-Tracker.md',
          category: 'Features',
          children: null,
        },
        {
          id: 'push-notifications',
          title: 'Push Notifications',
          filename: 'Push-Notifications.md',
          category: 'Features',
          children: null,
        },
        {
          id: 'race-to-royal-ring',
          title: 'Race to the Royal Ring',
          filename: 'Race-to-the-Royal-Ring.md',
          category: 'Features',
          children: null,
        },
        // More manuals can be added here as they're created
      ]

      this.manuals = manualManifest
      this.manualTree = this.buildTree(manualManifest)

      // Expand first category by default
      if (this.manualTree.length > 0) {
        this.expandedSections = [this.manualTree[0].id]
      }
    } catch (error) {
      console.error('Failed to load manuals:', error)
      this.manuals = []
      this.manualTree = []
    } finally {
      this.isLoading = false
    }
  }

  buildTree = manuals => {
    const tree = []
    const categoryMap = {}

    manuals.forEach(manual => {
      const category = manual.category || 'Other'
      if (!categoryMap[category]) {
        categoryMap[category] = {
          id: `category-${category.toLowerCase().replace(/\s+/g, '-')}`,
          title: category,
          type: 'category',
          children: [],
        }
        tree.push(categoryMap[category])
      }
      categoryMap[category].children.push(manual)
    })

    return tree
  }

  selectManual = async manualId => {
    // Find manual by ID
    const manual = this.manuals.find(m => m.id === manualId)
    if (!manual) {
      console.error('Manual not found:', manualId)
      return
    }

    // Check cache
    if (this.manualContentCache[manualId]) {
      this.selectedManual = this.manualContentCache[manualId]
      this.selectedManualPath = manualId
      return
    }

    this.isLoading = true
    try {
      // Fetch markdown file from API with authentication
      // Note: env.api.base_url already includes /api/ with trailing slash
      const apiUrl = `${env.api.base_url}user-manuals/${manual.filename}`

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: 'Bearer ' + AuthService.getToken(),
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to load manual: ${response.statusText}`)
      }

      const content = await response.text()

      const manualData = {
        ...manual,
        content,
      }

      this.manualContentCache[manualId] = manualData
      this.selectedManual = manualData
      this.selectedManualPath = manualId
    } catch (error) {
      console.error('Failed to load manual:', error)
      this.selectedManual = null
      this.selectedManualPath = null
    } finally {
      this.isLoading = false
    }
  }

  search = query => {
    this.searchQuery = query

    if (!query || query.trim() === '') {
      this.searchResults = []
      return
    }

    const normalizedQuery = query.toLowerCase().trim()

    if (this.searchMode === 'global') {
      // Search across all manuals
      const results = []
      this.manuals.forEach(manual => {
        if (manual.title.toLowerCase().includes(normalizedQuery)) {
          results.push({
            manual,
            type: 'title',
            match: manual.title,
          })
        }
      })
      this.searchResults = results
    } else {
      // Search within current manual
      if (!this.selectedManual || !this.selectedManual.content) {
        this.searchResults = []
        return
      }

      const content = this.selectedManual.content
      const lines = content.split('\n')
      const results = []

      lines.forEach((line, index) => {
        if (line.toLowerCase().includes(normalizedQuery)) {
          results.push({
            line: index + 1,
            text: line.trim(),
            match: line,
          })
        }
      })

      this.searchResults = results
    }
  }

  setSearchMode = mode => {
    this.searchMode = mode
    if (this.searchQuery) {
      this.search(this.searchQuery)
    }
  }

  toggleSection = sectionId => {
    const index = this.expandedSections.indexOf(sectionId)
    if (index > -1) {
      this.expandedSections = this.expandedSections.filter(id => id !== sectionId)
    } else {
      this.expandedSections = [...this.expandedSections, sectionId]
    }
  }

  clearSearch = () => {
    this.searchQuery = ''
    this.searchResults = []
  }
}

export default new UserManualsStore()
